'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const React = require('react');
const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const date = require('@zapier/date');
const utils_createUniqueCustomProperty = require('../../../utils/createUniqueCustomProperty.cjs.js');
const theme_Animation = require('../../../theme/Animation.cjs.js');
const theme_Colors = require('../../../theme/Colors.cjs.js');
const theme_Shadows = require('../../../theme/Shadows.cjs.js');
require('../../../theme/Typography.cjs.js');
const theme_LegacyTypography = require('../../../theme/LegacyTypography.cjs.js');
const components_display_Avatar_Avatar = require('../../display/Avatar/Avatar.cjs.js');
require('clsx');
const components_display_Avatar_AvatarsWithText = require('../../display/Avatar/AvatarsWithText.cjs.js');
const components_display_Heading_Heading = require('../../display/Heading/Heading.cjs.js');
const components_display_Heading_HeadingLevel = require('../../display/Heading/HeadingLevel.cjs.js');
const components_display_Img_Img = require('../../display/Img/Img.cjs.js');
const components_navigation_Link_Link = require('../../navigation/Link/Link.cjs.js');
const components_display_Icon_Icon = require('../../display/Icon/Icon.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const _interopDefault = e => e && e.__esModule ? e : { default: e };

const React__default = /*#__PURE__*/_interopDefault(React);

const labelBackgroundColorConfig = {
  primary: "UiPrimaryWeakest",
  secondary: "BrandPeach",
  neutral: "GrayWarm3"
};
const Vars = utils_createUniqueCustomProperty.createUniqueCustomProperties("Article", [
  "imageMarginBottom",
  "titleMargin",
  "labelBackgroundColor"
]);
const Styles = styleEncapsulation.labelStyles("Article", {
  root: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    position: relative;
    background: ${theme_Colors.Colors.PrimeWhite};
    border: 1px solid ${theme_Colors.Colors.GrayWarm4};
    transition-property: box-shadow;
    transition-timing-function: ${theme_Animation.Animation.transitionTimingFunction};
    transition-duration: ${theme_Animation.Animation.transitionDuration};
    border-radius: 3px;
    &:hover,
    &:focus,
    &:focus-within {
      color: ${theme_Colors.Colors.GrayWarm10};
      box-shadow: ${theme_Shadows.Shadows.elevation10};
    }

    &[data-layout='card'] {
      flex-direction: column;
    }

    &[data-layout='row'] {
      flex-direction: row;
      align-items: center;
      padding: 30px 0;
    }

    &[data-size='medium'] {
      ${Vars.imageMarginBottom}: 25px;
      ${Vars.titleMargin}: 20px;
      height: 100%;
    }
  `,
  image: react.css`
    ${styleMixins_reset.boxSizing};
    position: relative;
    overflow: hidden;
    border-radius: 3px 3px 0 0;

    &[data-layout='card'] {
      margin-bottom: var(${Vars.imageMarginBottom});
      padding-bottom: 50%; // 2:1 ratio
    }

    &[data-layout='row'] {
      border-radius: 3px;
      margin-left: 30px;
      margin-right: 30px;
      width: 370px;
      height: 185px; // 2:1 ratio
    }
  `,
  // `Styles.image` creates a box with a 2:1 ratio as defined
  // by design and this node centers the image within that box.
  imageInner: react.css`
    ${styleMixins_reset.boxSizing};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: 100%;
  `,
  content: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    flex-direction: column;
    border-radius: 0 0 3px 3px;
    padding: 0 20px 30px;
    flex: unset;

    &[data-showimg='false'] {
      padding: 30px 20px;
    }

    &[data-layout='row'] {
      padding: 0;
      border-radius: 0;
      flex: 1;
    }

    /* if the card is in row layout with no image we need to apply horizontal padding to the content */
    &[data-rownoimg='true'] {
      padding: 0 30px;
    }
  `,
  title: react.css`
    ${styleMixins_reset.baseReset};
    &[class] {
      order: 2;
      ${theme_LegacyTypography.LegacyTypography.sectionHeader3Semibold};
      color: ${theme_Colors.Colors.GrayWarm10};
      margin-bottom: var(${Vars.titleMargin});
      transition: ${theme_Animation.Animation.transitionValue};
      text-decoration: none;
      &:hover,
      &:focus {
        color: ${theme_Colors.Colors.GrayWarm10};
      }

      // This pseudo element stretches over the entire Article
      // to make the entire Article clickable while preserving
      // accessibilty by not nesting <a /> tags.
      &::before {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }

      &[data-lastelement='true'] {
        margin-bottom: 0;
      }
    }
  `,
  metaInfo: react.css`
    ${styleMixins_reset.boxSizing};
    // Ensure meta info renders above rest of clickable Article
    // in case any of the meta info is interactive.
    position: relative;
    z-index: 2;
    order: 4;
    display: flex;
    align-items: center;
    color: ${theme_Colors.Colors.GrayWarm7};
    ${theme_LegacyTypography.LegacyTypography.smallPrint1};
  `,
  meta: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  metaItem: react.css`
    ${styleMixins_reset.boxSizing};
    display: flex;
    align-items: center;
    &:not(:last-child) {
      &::after {
        content: '•';
        margin: 0 5px;
      }
    }
  `,
  description: react.css`
    ${styleMixins_reset.boxSizing};
    order: 3;
    margin-bottom: 20px;
    color: ${theme_Colors.Colors.GrayWarm9};

    &[data-size='medium'] {
      ${theme_LegacyTypography.LegacyTypography.paragraph3};
    }

    &[data-layout='row'] {
      ${theme_LegacyTypography.LegacyTypography.paragraph1Short};
    }

    &[data-lastelement='true'] {
      margin-bottom: 0;
    }
  `,
  category: react.css`
    ${styleMixins_reset.boxSizing};
    order: 1;
    position: relative;
    // Ensure clickable category renders above rest of clickable Article
    z-index: 2;
    ${theme_LegacyTypography.LegacyTypography.smallPrint1Bold};
    margin-bottom: 10px;
  `,
  label: (labelVariant = "neutral") => react.css`
    ${styleMixins_reset.boxSizing};
    order: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    text-align: center;
    ${Vars.labelBackgroundColor}: ${theme_Colors.Colors[labelBackgroundColorConfig[labelVariant]]};
    background-color: var(${Vars.labelBackgroundColor});
    border-radius: 100px;
    ${theme_LegacyTypography.LegacyTypography.paragraph3Semibold};
    color: ${theme_Colors.Colors.GrayWarm10};
    padding: 4px 8px;
    margin-bottom: 10px;
    width: max-content;
  `
});
const ArticleTag = ({ label, labelIcon = "fileFilled", labelVariant = "neutral" }) => {
  return react.jsx(
    "span",
    { css: Styles.label(labelVariant), "data-zds": true },
    labelIcon && react.jsx(components_display_Icon_Icon.Icon, { name: labelIcon, size: 18 }),
    react.jsx("span", { "data-zds": true }, label)
  );
};
const Article = (props) => {
  return react.jsx(
    "article",
    { css: Styles.root, "data-layout": props.layout, "data-size": "medium", "data-zds": true },
    props.imageUrl && react.jsx(
      "div",
      { css: Styles.image, "data-layout": props.layout, "data-zds": true },
      react.jsx(
        "div",
        { css: Styles.imageInner, "data-zds": true },
        react.jsx(
          components_display_Img_Img.Img,
          {
            // `aria-hidden` and empty `alt` are applied since we do not describe
            // our images for screen readers. Understanding the content of the image
            // is not critical to understanding the content of the article.
            alt: "",
            "aria-hidden": true,
            aspectRatio: "2:1",
            borderRadius: "none",
            isBlock: true,
            loading: "lazy",
            objectFit: "cover",
            sizes: "100vw",
            src: props.imageUrl
          }
        )
      )
    ),
    react.jsx(
      "div",
      { css: Styles.content, "data-layout": props.layout, "data-showimg": !!props.imageUrl, "data-rownoimg": props.layout === "row" && !props.imageUrl, "data-zds": true },
      react.jsx(
        components_display_Heading_HeadingLevel.HeadingLevel,
        null,
        react.jsx(
          components_navigation_Link_Link.Link,
          { css: Styles.title, color: null, href: props.url, "data-lastelement": !props.author && !props.description },
          react.jsx(components_display_Heading_Heading.Heading, null, props.title)
        )
      ),
      props.description && react.jsx("div", { css: Styles.description, "data-size": "medium", "data-layout": props.layout, "data-lastelement": !props.author, "data-zds": true }, props.description),
      props.author && react.jsx(
        "div",
        { css: Styles.metaInfo },
        react.jsx(
          components_display_Avatar_AvatarsWithText.AvatarsWithText,
          { avatars: react.jsx(components_display_Avatar_Avatar.Avatar, { "aria-hidden": true, isBlock: true, name: props.author.name, size: "medium", url: props.author.imageUrl }), multiline: true, size: "medium" },
          react.jsx(components_display_Avatar_AvatarsWithText.AvatarText, { "aria-label": `by ${props.author.name}`, kind: "heavy" }, props.author.name),
          props.meta
        )
      ),
      props.label && react.jsx(ArticleTag, { label: props.label, labelIcon: props.labelIcon, labelVariant: props.labelVariant }),
      props.category && props.categoryUrl && !props.label && react.jsx(
        "div",
        { css: Styles.category, "data-zds": true },
        react.jsx(components_navigation_Link_Link.Link, { "aria-label": `Categorized under ${props.category}`, href: props.categoryUrl }, props.category)
      )
    )
  );
};
const ArticleMeta = (props) => {
  const children = React__default.default.Children.toArray(props.children).filter(Boolean).map((child, i) => {
    return react.jsx("span", { css: Styles.metaItem, key: i, "data-zds": true }, child);
  });
  return react.jsx(
    components_display_Avatar_AvatarsWithText.AvatarText,
    { kind: "light" },
    react.jsx("span", { css: Styles.meta, "data-zds": true }, children)
  );
};
const ArticleReadTime = (props) => {
  return react.jsx(
    "div",
    { "data-zds": true },
    props.minutes,
    " min read"
  );
};
const ArticleDate = (props) => {
  return react.jsx(date.DateTime, { date: props.date });
};

exports.Article = Article;
exports.ArticleDate = ArticleDate;
exports.ArticleMeta = ArticleMeta;
exports.ArticleReadTime = ArticleReadTime;
//# sourceMappingURL=Article.cjs.js.map
